import { ClientError, errorMessages } from '@/lib/client-error';
import { type BlobStorageImage, uploadImageWith } from '@lib/files';
import { defineStore } from 'pinia';

type ProgressListener = (percentage: number) => void;

export const useCompanies = defineStore('companies', {
  actions: {
    async uploadFormData(formData: FormData, progress?: ProgressListener): Promise<string> {
      if (
        !formData.has('file') ||
        !(formData.get('file') instanceof File) ||
        (formData.get('file') as File).size === 0
      ) {
        throw new ClientError(errorMessages.upload_file_missing);
      }

      return await this.$api.company_UploadFile(formData.get('file') as File, {
        onUploadProgress: function (progressEvent) {
          const percentage = parseInt(
            Math.round((progressEvent.loaded * 100) / (progressEvent.total ?? 1)).toString(10),
            10
          );

          progress?.(percentage);
        },
      });
    },

    async uploadFile(file: File, progress?: ProgressListener) {
      const formData = new FormData();
      formData.append('file', file);

      return this.uploadFormData(formData, progress);
    },

    async uploadFileIncludeSizeAndName(file: File, progress?: ProgressListener) {
      const formData = new FormData();
      formData.append('file', file);

      return {
        filename: file.name,
        size: file.size,
        path: await this.uploadFormData(formData, progress),
      };
    },

    async uploadImage(file: File, progress?: ProgressListener): Promise<BlobStorageImage> {
      return uploadImageWith(file, (image) => this.uploadFile(image, progress));
    },
  },
});
